import { useState, useCallback } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { GripVertical, Plus, Pencil } from 'lucide-react';
import { LookupForm } from './LookupForm';
import { LookupDeleteButton } from './LookupDeleteButton';
import { useLookups } from '../hooks/useLookups';
import { useCreateLookup } from '../hooks/useCreateLookup';
import { useUpdateLookup } from '../hooks/useUpdateLookup';
import { useDeleteLookup } from '../hooks/useDeleteLookup';
import { useReorderLookups } from '../hooks/useReorderLookups';
import type { LookupDto, LookupType, CreateLookupRequest, UpdateLookupRequest } from '../types/lookup';

interface LookupTableProps {
  /** The type of lookup to display */
  lookupType: LookupType;
  /** Title for the table */
  title: string;
}

/**
 * Table component for managing lookup items
 * Features:
 * - Drag-and-drop reordering
 * - Inline create/edit forms
 * - Show/hide inactive toggle
 * - Deactivate (soft delete) with confirmation
 */
export function LookupTable({ lookupType, title }: LookupTableProps) {
  const [showInactive, setShowInactive] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Fetch lookups
  const {
    data: lookups,
    isLoading,
    error: fetchError,
    refetch,
    setData,
  } = useLookups(lookupType, { includeInactive: showInactive });

  // Mutations
  const { create, isLoading: isCreating, error: createError } = useCreateLookup(
    lookupType,
    () => {
      setIsAddingNew(false);
      refetch();
    }
  );

  const { update, isLoading: isUpdating, error: updateError } = useUpdateLookup(
    lookupType,
    () => {
      setEditingId(null);
      refetch();
    }
  );

  const { deleteLookup, isLoading: isDeleting, error: deleteError } = useDeleteLookup(
    lookupType,
    () => {
      refetch();
    }
  );

  const { reorder, error: reorderError } = useReorderLookups(lookupType);

  // Handle drag end for reordering
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      if (sourceIndex === destIndex) return;

      // Optimistic update
      const newLookups = [...lookups];
      const [removed] = newLookups.splice(sourceIndex, 1);
      newLookups.splice(destIndex, 0, removed);
      setData(newLookups);

      // Call API
      try {
        await reorder(newLookups.map((l) => l.id));
      } catch {
        // Revert on error
        refetch();
      }
    },
    [lookups, setData, reorder, refetch]
  );

  // Handle create
  const handleCreate = async (data: CreateLookupRequest | UpdateLookupRequest) => {
    await create(data as CreateLookupRequest);
  };

  // Handle update
  const handleUpdate = async (id: number, data: CreateLookupRequest | UpdateLookupRequest) => {
    await update(id, data as UpdateLookupRequest);
  };

  // Handle delete
  const handleDelete = async (lookup: LookupDto) => {
    await deleteLookup(lookup.id);
  };

  // Get the lookup being edited
  const editingLookup = editingId !== null
    ? lookups.find((l) => l.id === editingId)
    : null;

  // Combined error
  const error = fetchError || createError || updateError || deleteError || reorderError;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <LoadingSpinner label="Loading items..." />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`show-inactive-${lookupType}`}
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label
                htmlFor={`show-inactive-${lookupType}`}
                className="text-sm font-normal cursor-pointer"
              >
                Show inactive
              </Label>
            </div>
            <Button
              onClick={() => {
                setIsAddingNew(true);
                setEditingId(null);
              }}
              disabled={isAddingNew}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ErrorAlert error={error} className="mb-4" />

        {/* Add new form */}
        {isAddingNew && (
          <div className="mb-4 p-4 border rounded-lg bg-muted/50">
            <LookupForm
              onSubmit={handleCreate}
              onCancel={() => setIsAddingNew(false)}
              isSubmitting={isCreating}
              error={createError}
              inline
            />
          </div>
        )}

        {lookups.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No items found. Click "Add Item" to create one.
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`lookup-table-${lookupType}`}>
              {(provided) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {lookups.map((lookup, index) => (
                      <Draggable
                        key={lookup.id}
                        draggableId={`lookup-${lookup.id}`}
                        index={index}
                        isDragDisabled={editingId !== null || isAddingNew}
                      >
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? 'bg-muted' : ''}
                          >
                            <TableCell>
                              <div
                                {...provided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing"
                              >
                                <GripVertical className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </TableCell>
                            <TableCell>
                              {editingId === lookup.id ? (
                                <LookupForm
                                  lookup={editingLookup}
                                  onSubmit={(data) => handleUpdate(lookup.id, data)}
                                  onCancel={() => setEditingId(null)}
                                  isSubmitting={isUpdating}
                                  error={updateError}
                                  inline
                                />
                              ) : (
                                <span className={!lookup.isActive ? 'text-muted-foreground' : ''}>
                                  {lookup.name}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingId !== lookup.id && (
                                <Badge
                                  variant={lookup.isActive ? 'default' : 'secondary'}
                                  className={
                                    lookup.isActive
                                      ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                                  }
                                >
                                  {lookup.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {editingId !== lookup.id && (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setEditingId(lookup.id);
                                      setIsAddingNew(false);
                                    }}
                                    title="Edit item"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <LookupDeleteButton
                                    lookup={lookup}
                                    onDelete={() => handleDelete(lookup)}
                                    isDeleting={isDeleting}
                                  />
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </CardContent>
    </Card>
  );
}
